import { NekoPosSdk } from 'neko-common';
import { nekoPos } from 'public-contracts';
import { configs } from '../configs';
import authProvider from '../authProvider';
import { authenticatedFetch } from '../utils/authenticatedFetch';

type Cost = nekoPos.Cost;
type CostCategory = nekoPos.CostCategory;

// Create SDK instance with proper authentication context
const sdk = new NekoPosSdk(configs.apiRoot);

// Set up the auth client for the SDK
sdk.createAuthClient(configs, () => {
  // This will be called when authentication is successful
  console.log('CostService Authentication successful');
});

export default class CostService {
  private cost = sdk.cost;
  private apiRoot = configs.apiRoot;

  async getCosts(params?: {
    page?: number;
    perPage?: number;
    size?: number;
    filter?: any;
    sort?: any;
    q?: string;
  }): Promise<Cost[] & { __contentRange?: string; __total?: number }> {
    try {
      const queryParams = new URLSearchParams();

      // CRITICAL: API requires BOTH page AND size to be present!
      // If only size is provided, it defaults to size=10
      const page = params?.page ?? 1;
      let size = 20; // Default size

      // Determine size from various parameter names
      if (params?.size) {
        size = params.size;
      } else if (params?.perPage) {
        size = params.perPage;
      }

      // Always set both page and size
      queryParams.append('page', page.toString());
      queryParams.append('size', size.toString());

      if (params?.q) queryParams.append('q', params.q);
      if (params?.filter)
        queryParams.append('filter', JSON.stringify(params.filter));
      if (params?.sort) queryParams.append('sort', JSON.stringify(params.sort));

      const url = `${this.apiRoot}/costs${
        queryParams.toString() ? `?${queryParams.toString()}` : ''
      }`;

      console.log(`🔍 Fetching costs from: ${url}`);
      console.log(`📊 Request params: page=${page}, size=${size}`);

      const response = await authenticatedFetch(url, { method: 'GET' });

      if (!response.ok) {
        const error = await response
          .json()
          .catch(() => ({ message: response.statusText }));
        throw new Error(
          error.message || `Failed to fetch costs: ${response.statusText}`
        );
      }

      const result = await response.json();
      console.log(
        `📦 API Response type:`,
        Array.isArray(result) ? 'Array' : typeof result
      );
      console.log(
        `📦 API Response length:`,
        Array.isArray(result) ? result.length : result.data?.length || 0
      );

      // Check Content-Range header for total count
      const contentRange = response.headers.get('Content-Range');
      let total = Array.isArray(result)
        ? result.length
        : result.data?.length || 0;
      if (contentRange) {
        const match = contentRange.match(/\/(\d+)$/);
        if (match) {
          total = parseInt(match[1], 10);
        }
      }

      console.log(
        `✅ Loaded ${
          Array.isArray(result) ? result.length : result.data?.length || 0
        } costs (page ${page}, total: ${total})`
      );

      const costs = Array.isArray(result) ? result : result.data || [];

      // Attach pagination metadata to the array
      const resultWithMeta = costs as Cost[] & {
        __contentRange?: string;
        __total?: number;
      };
      resultWithMeta.__contentRange = contentRange || undefined;
      resultWithMeta.__total = total;

      return resultWithMeta;
    } catch (error) {
      console.error('Error fetching costs:', error);
      throw error;
    }
  }

  async getCost(id: string): Promise<Cost> {
    try {
      const cost = await this.cost.getCost(id);
      return cost;
    } catch (error) {
      console.error('Error fetching cost:', error);
      throw error;
    }
  }

  async createCost(costData: Omit<Cost, 'id'>): Promise<Cost> {
    try {
      const newCost = await this.cost.createCost(costData);
      return newCost;
    } catch (error) {
      console.error('Error creating cost:', error);
      throw error;
    }
  }

  async updateCost(id: string, costData: Partial<Cost>): Promise<Cost> {
    try {
      const updatedCost = await this.cost.updateCost(id, costData);
      return updatedCost;
    } catch (error) {
      console.error('Error updating cost:', error);
      throw error;
    }
  }

  async deleteCost(id: string): Promise<void> {
    try {
      await this.cost.deleteCost(id);
    } catch (error) {
      console.error('Error deleting cost:', error);
      throw error;
    }
  }

  async getTotalAmount(params?: {
    filter?: any;
    q?: string;
  }): Promise<number> {
    try {
      // Fetch all matching costs with a large page size to calculate total
      // This respects the same filters and search as the main query
      const result = await this.getCosts({
        page: 1,
        size: 10000, // Large size to get all matching costs
        q: params?.q,
        filter: params?.filter,
      });

      const costsArray = Array.isArray(result) ? result : [];
      
      // Sum all amounts
      const total = costsArray.reduce((sum, cost) => {
        const amount =
          typeof cost.amount === 'number'
            ? cost.amount
            : parseFloat(String(cost.amount).replace(/,/g, '')) || 0;
        return sum + amount;
      }, 0);

      return total;
    } catch (error) {
      console.error('Error fetching total amount:', error);
      // Return 0 on error to avoid breaking the UI
      return 0;
    }
  }

  async getCostCategories(params?: {
    page?: number;
    perPage?: number;
    size?: number;
    filter?: any;
    sort?: any;
  }): Promise<
    any[] & { __total?: number; __page?: number; __perPage?: number }
  > {
    try {
      const queryParams = new URLSearchParams();

      // CRITICAL: API requires BOTH page AND size to be present!
      // If only size is provided, it defaults to size=10
      const page = params?.page ?? 1;
      let size = 20; // Default size (matching product categories)

      // Determine size from various parameter names
      if (params?.size) {
        size = params.size;
      } else if (params?.perPage) {
        size = params.perPage;
      }

      // Always set both page and size
      queryParams.append('page', page.toString());
      queryParams.append('size', size.toString());

      if (params?.filter)
        queryParams.append('filter', JSON.stringify(params.filter));
      if (params?.sort) queryParams.append('sort', JSON.stringify(params.sort));

      const url = `${this.apiRoot}/cost_categories${
        queryParams.toString() ? `?${queryParams.toString()}` : ''
      }`;
      console.log('Fetching cost categories from:', url);
      console.log(`📊 Request params: page=${page}, size=${size}`);

      const response = await authenticatedFetch(url, { method: 'GET' });

      console.log('Cost categories response status:', response.status);

      if (!response.ok) {
        const errorText = await response.text();
        console.error('Cost categories API error:', errorText);
        const error = await response
          .json()
          .catch(() => ({ message: response.statusText }));
        throw new Error(
          error.message ||
            `Failed to fetch cost categories: ${response.statusText}`
        );
      }

      const result = await response.json();
      console.log('Cost categories result:', result);
      const categories = Array.isArray(result) ? result : result.data || [];

      // Check Content-Range header for total count
      const contentRange = response.headers.get('Content-Range');
      let total = categories.length;
      if (contentRange) {
        const match = contentRange.match(/\/(\d+)$/);
        if (match) {
          total = parseInt(match[1], 10);
        }
      }

      console.log(
        `✅ Loaded ${categories.length} cost categories (page ${page}, total: ${total})`
      );

      // Attach pagination metadata to the array
      const resultWithMeta = categories as any[] & {
        __total?: number;
        __page?: number;
        __perPage?: number;
      };
      resultWithMeta.__total = total;
      resultWithMeta.__page = page;
      resultWithMeta.__perPage = size;

      return resultWithMeta;
    } catch (error) {
      console.error('Error fetching cost categories:', error);
      throw error;
    }
  }

  async getCostCategory(id: string): Promise<CostCategory> {
    try {
      const category = await this.cost.getCostCategory(id);
      return category;
    } catch (error) {
      console.error('Error fetching cost category:', error);
      throw error;
    }
  }

  async createCostCategory(
    categoryData: Omit<CostCategory, 'id'>
  ): Promise<CostCategory> {
    try {
      console.log(
        '🔧 CostService.createCostCategory called with:',
        categoryData
      );
      console.log('🔧 SDK cost service:', this.cost);
      const newCategory = await this.cost.createCostCategory(categoryData);
      console.log('✅ CostService.createCostCategory success:', newCategory);
      return newCategory;
    } catch (error) {
      console.error('❌ CostService.createCostCategory error:', error);
      console.error('❌ Error type:', error?.constructor?.name);
      console.error(
        '❌ Error message:',
        error instanceof Error ? error.message : String(error)
      );
      throw error;
    }
  }

  async updateCostCategory(
    id: string,
    categoryData: Partial<CostCategory>
  ): Promise<CostCategory> {
    try {
      const updatedCategory = await this.cost.updateCostCategory(
        id,
        categoryData
      );
      return updatedCategory;
    } catch (error) {
      console.error('Error updating cost category:', error);
      throw error;
    }
  }

  async deleteCostCategory(id: string): Promise<void> {
    try {
      await this.cost.deleteCostCategory(id);
    } catch (error) {
      console.error('Error deleting cost category:', error);
      throw error;
    }
  }
}
